import { sortByOrder } from '@strength/utils';
export function mergeWorkoutLayersByWorkoutId(...allLayers) {
    const workoutLayersByWorkoutId = new Map();
    for (const layers of allLayers) {
        for (const layer of layers) {
            const matchLayers = workoutLayersByWorkoutId.get(layer.workoutId);
            if (matchLayers) {
                matchLayers.push(layer);
            }
            else {
                workoutLayersByWorkoutId.set(layer.workoutId, [layer]);
            }
        }
    }
    const workouts = [];
    for (const workoutLayers of workoutLayersByWorkoutId.values()) {
        workouts.push(mergeWorkoutLayers(...workoutLayers));
    }
    return workouts;
}
/**
 * Merges workout layers, resolving duplicate set metrics using an explicit
 * source ranking. Earlier entries in `sourceRanking` take priority; `null`
 * represents internally-entered data (no dataSource). Sources absent from
 * the ranking are treated as lowest priority (first-encountered wins among
 * unranked sources).
 *
 * Example: `['Catapult', null, 'Match Stats']`
 *   → Catapult > internal > Match Stats > any other source
 */
export function mergeWorkoutLayersWithExternal(sourceRanking, base, ...rest) {
    return coreMergeWorkoutLayers(sourceRanking, base, ...rest);
}
export function mergeWorkoutLayers(base, ...rest) {
    // null first → internal data always wins over any external source
    return coreMergeWorkoutLayers([null], base, ...rest);
}
function coreMergeWorkoutLayers(sourceRanking, base, ...rest) {
    const workoutId = base.workoutId;
    let intermediateSections = [];
    let nameFromLayer = null;
    const workoutLayers = [base, ...rest];
    let assignedDateState = {
        assignedDate: base.assignedDate ?? null,
    };
    for (let i = 0; i < workoutLayers.length; i++) {
        // Base Layer is the first layer, it's the base of the workout
        const isBaseLayer = i === 0;
        const workoutLayer = workoutLayers[i];
        if (!workoutLayer) {
            continue;
        }
        // Overlay layers after `base`: position-based because synthetic wrappers
        // (e.g. mergeAthleteWorkoutFromLayers) reuse the same id for every layer.
        if (!isBaseLayer) {
            if (workoutLayer.excludeAssignedDate) {
                assignedDateState = { assignedDate: null };
            }
            else {
                assignedDateState = {
                    assignedDate: applyOverride('assignedDate', assignedDateState, workoutLayer),
                };
            }
        }
        if (isBaseLayer) {
            nameFromLayer = workoutLayer.name ?? null;
        }
        else if (workoutLayer.name != null) {
            nameFromLayer = workoutLayer.name;
        }
        const workoutLayerMaps = workoutLayerAsMap(workoutLayer.layer);
        if (workoutId !== workoutLayer.workoutId) {
            throw new Error('Invariant Violation: Cannot merge workout layers with different workoutIds');
        }
        const existingSectionIds = new Set(intermediateSections.map((s) => s.workoutSectionId));
        intermediateSections = intermediateSections
            .concat((workoutLayer?.layer.sectionAdds ?? [])
            .filter((s) => !existingSectionIds.has(s.workoutSectionId))
            .map((s) => formatAddedSection(workoutId, { ...s, status: 'planned' })))
            .filter((s) => !workoutLayerMaps.removeSectionIds.has(s.workoutSectionId))
            .map((s) => mergeEditsWithSection(s, workoutLayerMaps, sourceRanking));
    }
    const workout = {
        workoutId,
        assignedDate: assignedDateState.assignedDate,
        name: nameFromLayer,
        sections: sortByOrder(intermediateSections.map(transformIntermediateSection)),
    };
    return stripPrescriptionSentinel(workout);
}
// Removes the 'Prescription' sentinel from the merged output so it does not
// leak into the returned Workout. Applies to both the winning metric's
// dataSource and the `overwritten` snapshot's dataSource — a cross-source
// dedupe inside a prescription layer can capture 'Prescription' as the
// replaced source, and the pre-merge sentinel must never reach consumers.
function stripExercisePrescription(exercise) {
    return {
        ...exercise,
        sets: exercise.sets.map((set) => ({
            ...set,
            metrics: set.metrics.map((metric) => {
                const stripped = metric.dataSource === 'Prescription'
                    ? { ...metric, dataSource: undefined }
                    : metric;
                // A snapshot whose source was only the sentinel carries no real
                // source to display, so drop it rather than show 'Prescription'.
                if (stripped.overwritten?.dataSource === 'Prescription') {
                    const { overwritten: _overwritten, ...rest } = stripped;
                    return rest;
                }
                return stripped;
            }),
        })),
    };
}
function stripPrescriptionSentinel(workout) {
    return {
        ...workout,
        sections: workout.sections.map((section) => ({
            ...section,
            entries: section.entries.map((entry) => {
                if (entry.__type === 'WorkoutExercise') {
                    return stripExercisePrescription(entry);
                }
                if (entry.__type === 'WorkoutSuperset') {
                    return {
                        ...entry,
                        exercises: entry.exercises.map((grouped) => ({
                            ...grouped,
                            exercise: stripExercisePrescription(grouped.exercise),
                        })),
                    };
                }
                if (entry.__type === 'WorkoutAlternateGroup') {
                    return {
                        ...entry,
                        exercises: entry.exercises.map((grouped) => ({
                            ...grouped,
                            exercise: stripExercisePrescription(grouped.exercise),
                        })),
                    };
                }
                return entry;
            }),
        })),
    };
}
export function tagPrescriptionLayer(layer) {
    const setMetrics = layer?.layer?.setMetrics;
    if (!layer || !setMetrics?.length)
        return layer;
    return {
        ...layer,
        layer: {
            ...layer.layer,
            setMetrics: setMetrics.map((sm) => sm.dataSource == null
                ? { ...sm, dataSource: 'Prescription' }
                : sm),
        },
    };
}
function transformIntermediateSection({ exercises, supersets, alternateGroups, ...section }) {
    const entries = [];
    const workoutExerciseIdsOnSupersets = new Set();
    const exerciseById = new Map(exercises.map((exercise) => [exercise.workoutExerciseId, exercise]));
    for (const superset of supersets) {
        const supersetExercises = [];
        for (const { workoutExerciseId, sortOrder, workoutSupersetId, workoutSupersetExerciseId, } of superset.supersetExercises) {
            const matchingExercise = exerciseById.get(workoutExerciseId);
            if (matchingExercise) {
                supersetExercises.push({
                    workoutExerciseId,
                    sortOrder,
                    workoutSupersetId,
                    workoutSupersetExerciseId,
                    exercise: sortObjectsOnExercise(matchingExercise),
                });
                workoutExerciseIdsOnSupersets.add(workoutExerciseId);
            }
        }
        entries.push({
            __type: 'WorkoutSuperset',
            workoutSupersetId: superset.workoutSupersetId,
            workoutSectionId: superset.workoutSectionId,
            sortOrder: superset.sortOrder,
            exercises: sortByOrder(supersetExercises),
            status: superset.status,
        });
    }
    const workoutExerciseIdsOnAlternateGroups = new Set();
    for (const alternateGroup of alternateGroups) {
        const alternateExercises = [];
        for (const { workoutExerciseId, sortOrder, workoutAlternateGroupId, workoutAlternateExerciseId, } of alternateGroup.alternateExercises) {
            const matchingExercise = exerciseById.get(workoutExerciseId);
            if (matchingExercise) {
                alternateExercises.push({
                    workoutExerciseId,
                    sortOrder,
                    workoutAlternateGroupId,
                    workoutAlternateExerciseId,
                    exercise: sortObjectsOnExercise(matchingExercise),
                });
                workoutExerciseIdsOnAlternateGroups.add(workoutExerciseId);
            }
        }
        entries.push({
            __type: 'WorkoutAlternateGroup',
            workoutAlternateGroupId: alternateGroup.workoutAlternateGroupId,
            workoutSectionId: alternateGroup.workoutSectionId,
            sortOrder: alternateGroup.sortOrder,
            exercises: sortByOrder(alternateExercises),
            selectedExerciseId: alternateGroup.selectedExerciseId,
        });
    }
    for (const exercise of exercises) {
        if (!workoutExerciseIdsOnSupersets.has(exercise.workoutExerciseId) &&
            !workoutExerciseIdsOnAlternateGroups.has(exercise.workoutExerciseId)) {
            entries.push(sortObjectsOnExercise(exercise));
        }
    }
    return {
        ...section,
        entries: sortByOrder(entries),
    };
}
function sortObjectsOnExercise(exercise) {
    return {
        ...exercise,
        sets: sortByOrder(exercise.sets),
        metrics: sortByOrder(exercise.metrics),
    };
}
function formatAddedSection(workoutId, sectionAdd) {
    return {
        workoutId,
        name: sectionAdd.name,
        sortOrder: sectionAdd.sortOrder,
        workoutSectionId: sectionAdd.workoutSectionId,
        instruction: sectionAdd.instruction,
        color: sectionAdd.color,
        exercises: [],
        supersets: [],
        alternateGroups: [],
        status: sectionAdd.status,
    };
}
function mergeEditsWithSection(section, workoutLayer, sourceRanking) {
    const existingExerciseIds = new Set(section.exercises.map((e) => e.workoutExerciseId));
    const exerciseAdds = (workoutLayer.exerciseAddsBySectionId.get(section.workoutSectionId) ?? []).filter((add) => !existingExerciseIds.has(add.workoutExerciseId));
    const supersetAdds = workoutLayer.supersetAddsBySectionId.get(section.workoutSectionId) ?? [];
    const alternateGroupAdds = workoutLayer.alternateGroupAddsBySectionId.get(section.workoutSectionId) ??
        [];
    const sectionEdit = workoutLayer.sectionEditsById.get(section.workoutSectionId);
    return {
        ...section,
        status: sectionEdit?.status ?? 'planned',
        name: applyOverride('name', section, sectionEdit),
        sortOrder: applyOverride('sortOrder', section, sectionEdit),
        exercises: section.exercises
            .concat(exerciseAdds.map((e) => formatAddedExercise(section.workoutId, { ...e, status: 'planned' })))
            .filter((e) => !workoutLayer.removeExerciseIds.has(e.workoutExerciseId))
            .map((e) => mergeLayerWithExercise(e, workoutLayer, sourceRanking)),
        supersets: section.supersets
            .concat(supersetAdds.map((s) => formatAddedSuperset({ ...s, status: 'planned' })))
            .filter((s) => !workoutLayer.removeSupersetIds.has(s.workoutSupersetId))
            .map((s) => mergeLayerWithSuperset(s, workoutLayer)),
        alternateGroups: section.alternateGroups
            .concat(alternateGroupAdds.map((a) => formatAddedAlternateGroup(a)))
            .filter((a) => !workoutLayer.removeAlternateGroupIds.has(a.workoutAlternateGroupId))
            .map((a) => mergeLayerWithAlternateGroup(a, workoutLayer)),
        instruction: applyOverrideNullable('instruction', section, sectionEdit),
        color: applyOverrideNullable('color', section, sectionEdit),
    };
}
function formatAddedSuperset(supersetAdd) {
    return {
        __type: 'WorkoutSuperset',
        workoutSectionId: supersetAdd.workoutSectionId,
        workoutSupersetId: supersetAdd.workoutSupersetId,
        sortOrder: supersetAdd.sortOrder,
        supersetExercises: [],
        status: supersetAdd.status,
    };
}
function mergeLayerWithSuperset(superset, athleteMods) {
    const supersetEdit = athleteMods.supersetEditsById.get(superset.workoutSupersetId);
    const supersetExerciseAdds = athleteMods.supersetExerciseAddsBySupersetId.get(superset.workoutSupersetId) ?? [];
    return {
        ...superset,
        status: supersetEdit?.status ?? superset.status,
        sortOrder: applyOverride('sortOrder', superset, supersetEdit),
        supersetExercises: superset.supersetExercises
            .concat(supersetExerciseAdds)
            .filter((s) => !athleteMods.removeSupersetExerciseIds.has(s.workoutSupersetExerciseId))
            .map((s) => mergeLayerWithSupersetExercise(s, athleteMods)),
    };
}
function mergeLayerWithSupersetExercise(supersetExerciseAdd, athleteMods) {
    const supersetExerciseEdit = athleteMods.supersetExerciseEditsById.get(supersetExerciseAdd.workoutSupersetExerciseId);
    return {
        ...supersetExerciseAdd,
        sortOrder: applyOverride('sortOrder', supersetExerciseAdd, supersetExerciseEdit),
    };
}
function formatAddedAlternateGroup(alternateGroupAdd) {
    return {
        __type: 'WorkoutAlternateGroup',
        workoutSectionId: alternateGroupAdd.workoutSectionId,
        workoutAlternateGroupId: alternateGroupAdd.workoutAlternateGroupId,
        sortOrder: alternateGroupAdd.sortOrder,
        alternateExercises: [],
        selectedExerciseId: alternateGroupAdd.selectedExerciseId,
    };
}
function mergeLayerWithAlternateGroup(alternateGroup, athleteMods) {
    const alternateGroupEdit = athleteMods.alternateGroupEditsById.get(alternateGroup.workoutAlternateGroupId);
    const alternateExerciseAdds = athleteMods.alternateExerciseAddsByAlternateGroupId.get(alternateGroup.workoutAlternateGroupId) ?? [];
    return {
        ...alternateGroup,
        selectedExerciseId: applyOverride('selectedExerciseId', alternateGroup, alternateGroupEdit),
        sortOrder: applyOverride('sortOrder', alternateGroup, alternateGroupEdit),
        alternateExercises: alternateGroup.alternateExercises
            .concat(alternateExerciseAdds)
            .filter((a) => !athleteMods.removeAlternateExerciseIds.has(a.workoutAlternateExerciseId))
            .map((a) => mergeLayerWithAlternateExercise(a, athleteMods)),
    };
}
function mergeLayerWithAlternateExercise(alternateExerciseAdd, athleteMods) {
    const alternateExerciseEdit = athleteMods.alternateExerciseEditsById.get(alternateExerciseAdd.workoutAlternateExerciseId);
    return {
        ...alternateExerciseAdd,
        sortOrder: applyOverride('sortOrder', alternateExerciseAdd, alternateExerciseEdit),
    };
}
function formatAddedExercise(workoutId, exerciseAdd) {
    return {
        __type: 'WorkoutExercise',
        workoutId,
        workoutExerciseId: exerciseAdd.workoutExerciseId,
        libraryExerciseId: exerciseAdd.libraryExerciseId,
        exerciseName: exerciseAdd.exerciseName,
        instruction: exerciseAdd.instruction,
        sortOrder: exerciseAdd.sortOrder,
        workoutSectionId: exerciseAdd.workoutSectionId,
        sets: [],
        metrics: [],
        status: exerciseAdd.status,
        isEachSide: exerciseAdd.isEachSide,
    };
}
function mergeLayerWithExercise(exercise, athleteMods, sourceRanking) {
    const exerciseEdit = athleteMods.exerciseEditById.get(exercise.workoutExerciseId);
    const existingSetIds = new Set(exercise.sets.map((s) => s.workoutSetId));
    const setAdds = (athleteMods.setAddsByExerciseId.get(exercise.workoutExerciseId) ?? []).filter((add) => !existingSetIds.has(add.workoutSetId));
    const exerciseMetricAdds = athleteMods.exerciseMetricAddsByExerciseId.get(exercise.workoutExerciseId) ?? [];
    // Merge metrics with unique metricIds, preferring athleteMods when duplicates exist
    const metricsMap = new Map(exercise.metrics.map((m) => [m.metricId, m]));
    exerciseMetricAdds.forEach((metricAdd) => {
        metricsMap.set(metricAdd.metricId, formatAddedExerciseMetric(metricAdd));
    });
    const mergedMetrics = Array.from(metricsMap.values());
    return {
        ...exercise,
        libraryExerciseId: applyOverride('libraryExerciseId', exercise, exerciseEdit),
        exerciseName: applyOverride('exerciseName', exercise, exerciseEdit),
        instruction: applyOverrideNullable('instruction', exercise, exerciseEdit),
        isEachSide: applyOverrideNullable('isEachSide', exercise, exerciseEdit),
        sortOrder: applyOverride('sortOrder', exercise, exerciseEdit),
        status: exerciseEdit?.status ?? 'planned',
        metrics: mergedMetrics.filter((m) => !athleteMods.removeExerciseMetricIds.has(m.workoutExerciseMetricId)),
        sets: exercise.sets
            .concat(setAdds.map((s) => formatAddedSet({ ...s, status: 'planned' })))
            .filter((s) => !athleteMods.removeSetIds.has(s.workoutSetId))
            .map((s) => mergeLayerWithSet(s, athleteMods, sourceRanking)),
    };
}
function formatAddedExerciseMetric(metricAdd) {
    return {
        workoutExerciseMetricId: metricAdd.workoutExerciseMetricId,
        metricId: metricAdd.metricId,
        metricName: metricAdd.metricName,
        metricType: metricAdd.metricType,
        workoutExerciseId: metricAdd.workoutExerciseId,
        benchmarkGridId: metricAdd.benchmarkGridId,
        sortOrder: metricAdd.sortOrder,
    };
}
function formatAddedSet(setAdd) {
    return {
        workoutSetId: setAdd.workoutSetId,
        sortOrder: setAdd.sortOrder,
        workoutExerciseId: setAdd.workoutExerciseId,
        metrics: [],
        status: setAdd.status,
        workoutSetGroupId: setAdd.workoutSetGroupId,
    };
}
function getSetMetricRank(sourceRanking, dataSource) {
    const index = sourceRanking.indexOf(dataSource ?? null);
    // Sources not in the ranking are treated as lowest priority
    return index === -1 ? sourceRanking.length : index;
}
function setMetricWins(candidate, current, sourceRanking, tiesDisplace, considerRecency) {
    const candidateRank = getSetMetricRank(sourceRanking, candidate.dataSource);
    const currentRank = getSetMetricRank(sourceRanking, current.dataSource);
    if (candidateRank !== currentRank)
        return candidateRank < currentRank;
    // Same source priority — within a layer the more recently entered value
    // wins. Compare as instants, not strings: ISO strings may differ in
    // precision or offset.
    if (considerRecency && candidate.enteredOn && current.enteredOn) {
        const candidateTime = Date.parse(candidate.enteredOn);
        const currentTime = Date.parse(current.enteredOn);
        if (!Number.isNaN(candidateTime) && !Number.isNaN(currentTime)) {
            if (candidateTime !== currentTime)
                return candidateTime > currentTime;
            return tiesDisplace;
        }
    }
    return tiesDisplace;
}
/**
 * Attaches the entry a winner replaced so consumers can show the overwritten
 * source + value in the hover. Only cross-source overwrites are captured —
 * the display is "Source: <original> (<value>)" and adds nothing when the
 * source is unchanged.
 */
function withOverwritten(winner, replaced) {
    // Same source — nothing new was overwritten, but keep any snapshot the
    // replaced entry already carried (e.g. Catapult → Manual, then Manual re-edit).
    if (winner.dataSource === replaced.dataSource) {
        return replaced.overwritten
            ? { ...winner, overwritten: replaced.overwritten }
            : winner;
    }
    return {
        ...winner,
        overwritten: {
            dataSource: replaced.dataSource,
            input: replaced.input ?? '',
            enteredBy: replaced.enteredBy,
            enteredOn: replaced.enteredOn,
        },
    };
}
/**
 * Deduplicates set metrics by metricId using an explicit source ranking.
 * When two entries share the same metricId, the one whose source appears
 * earlier in `sourceRanking` wins — unless both carry `enteredOn`, in which
 * case the more recently entered value wins. `null` in the ranking represents
 * internally-entered data (no dataSource). Sources absent from the ranking
 * are treated as lowest priority. An empty ranking means no preference —
 * first-encountered wins. The entry a winner replaced (when from a different
 * source) is retained on the winner via `overwritten`.
 */
function dedupeLayerSetMetrics(setMetrics, sourceRanking) {
    const byMetricId = new Map();
    for (const sm of setMetrics) {
        const existing = byMetricId.get(sm.metricId);
        if (!existing) {
            byMetricId.set(sm.metricId, sm);
        }
        else if (setMetricWins(sm, existing, sourceRanking, false, true)) {
            byMetricId.set(sm.metricId, withOverwritten(sm, existing));
        }
    }
    return [...byMetricId.values()];
}
function mergeLayerWithSet(set, athleteMods, sourceRanking) {
    const setEdit = athleteMods.setEditsByExerciseId
        .get(set.workoutExerciseId)
        ?.find((edit) => edit.workoutSetId === set.workoutSetId);
    const rawSetMetrics = athleteMods.setMetricsBySetId.get(set.workoutSetId) ?? [];
    const setMetrics = dedupeLayerSetMetrics(rawSetMetrics, sourceRanking);
    for (const setMetric of setMetrics) {
        const match = set.metrics.find((m) => m.metricId === setMetric.metricId);
        if (match) {
            // Only overwrite if the incoming metric has equal or higher priority.
            // This is a cross-layer comparison, so layer order decides ties — never
            // `enteredOn` (see `setMetricWins`).
            if (setMetricWins(setMetric, match, sourceRanking, true, false)) {
                // `match` is the entry the current merge is displacing — prefer a
                // snapshot of it over `setMetric.overwritten`, which only describes
                // an earlier same-layer replacement. Falls back to the existing
                // snapshot when the source is unchanged by this replacement. A
                // displaced entry with no displayable source (the 'Prescription'
                // sentinel or an untagged prescription value) says nothing consumers
                // can render, so keep the winner's own snapshot instead.
                const matchHasDisplayableSource = !!match.dataSource && match.dataSource !== 'Prescription';
                const replacedSnapshot = !matchHasDisplayableSource
                    ? setMetric.overwritten
                    : match.dataSource !== setMetric.dataSource
                        ? {
                            dataSource: match.dataSource,
                            input: match.input,
                            enteredBy: match.enteredBy,
                            enteredOn: match.enteredOn,
                        }
                        : match.overwritten ?? setMetric.overwritten;
                match.input = applyOverride('input', match, setMetric);
                match.forCollection = applyOverride('forCollection', match, setMetric);
                match.reps = applyOverride('reps', match, setMetric);
                // Assign dataSource directly — applyOverride treats undefined as "no
                // change", but when the winning metric has no dataSource we must clear
                // whatever the existing value was (e.g. 'Prescription' sentinel).
                match.dataSource = setMetric.dataSource;
                // Provenance rides with the winning metric the same way: assign
                // directly so a winner without provenance clears a loser's stale one.
                match.enteredBy = setMetric.enteredBy;
                match.enteredOn = setMetric.enteredOn;
                match.overwritten = replacedSnapshot;
            }
        }
        else {
            set.metrics.push({
                metricId: setMetric.metricId,
                input: setMetric.input ?? '',
                forCollection: setMetric.forCollection ?? false,
                reps: setMetric.reps,
                dataSource: setMetric.dataSource,
                enteredBy: setMetric.enteredBy,
                enteredOn: setMetric.enteredOn,
                overwritten: setMetric.overwritten,
            });
        }
    }
    return {
        ...set,
        status: setEdit?.status ?? 'planned',
        sortOrder: setEdit?.sortOrder ?? set.sortOrder,
    };
}
function applyOverride(key, entity, override) {
    const overrideValue = override?.[key];
    if (overrideValue === null || overrideValue === undefined) {
        return entity[key];
    }
    return overrideValue;
}
function applyOverrideNullable(key, entity, override) {
    const overrideValue = override?.[key];
    if (overrideValue === undefined) {
        return entity[key];
    }
    return overrideValue;
}
function groupBy(items, keySelector) {
    const map = new Map();
    for (const item of items) {
        const key = keySelector(item);
        const existing = map.get(key);
        if (existing) {
            existing.push(item);
        }
        else {
            map.set(key, [item]);
        }
    }
    return map;
}
function uniqueBy(items, keySelector) {
    const seen = new Set();
    return items.filter((item) => {
        const key = keySelector(item);
        if (seen.has(key)) {
            return false;
        }
        seen.add(key);
        return true;
    });
}
function workoutLayerAsMap(layer) {
    if (!layer) {
        return {
            sectionAdds: [],
            sectionEditsById: new Map(),
            removeSectionIds: new Set(),
            supersetAddsBySectionId: new Map(),
            supersetEditsById: new Map(),
            removeSupersetIds: new Set(),
            supersetExerciseAddsBySupersetId: new Map(),
            supersetExerciseEditsById: new Map(),
            removeSupersetExerciseIds: new Set(),
            alternateGroupAddsBySectionId: new Map(),
            alternateGroupEditsById: new Map(),
            removeAlternateGroupIds: new Set(),
            alternateExerciseAddsByAlternateGroupId: new Map(),
            alternateExerciseEditsById: new Map(),
            removeAlternateExerciseIds: new Set(),
            exerciseAddsBySectionId: new Map(),
            exerciseEditById: new Map(),
            removeExerciseIds: new Set(),
            exerciseMetricAddsByExerciseId: new Map(),
            removeExerciseMetricIds: new Set(),
            setAddsByExerciseId: new Map(),
            setEditsByExerciseId: new Map(),
            removeSetIds: new Set(),
            setMetricsBySetId: new Map(),
        };
    }
    const { sectionAdds = [], sectionEdits = [], supersetAdds = [], supersetEdits = [], supersetExerciseAdds = [], supersetExerciseEdits = [], alternateGroupAdds = [], alternateGroupEdits = [], alternateExerciseAdds = [], alternateExerciseEdits = [], exerciseAdds = [], exerciseEdits = [], exerciseMetricAdds = [], setAdds = [], setEdits = [], setMetrics = [], removals = [], } = layer || {};
    const exerciseAddsBySectionId = groupBy(exerciseAdds, (add) => add.workoutSectionId);
    const supersetAddsBySectionId = groupBy(supersetAdds, (add) => add.workoutSectionId);
    // A group id must resolve to a single group. Layers written before the builder enforced that
    // can hold more than one `alternateGroupAdds` row per `workoutAlternateGroupId`, which would
    // otherwise render the group once per row.
    const alternateGroupAddsBySectionId = groupBy(uniqueBy(alternateGroupAdds, (add) => add.workoutAlternateGroupId), (add) => add.workoutSectionId);
    const supersetExerciseAddsBySupersetId = groupBy(supersetExerciseAdds, (add) => add.workoutSupersetId);
    const alternateExerciseAddsByAlternateGroupId = groupBy(alternateExerciseAdds, (add) => add.workoutAlternateGroupId);
    const exerciseMetricAddsByExerciseId = groupBy(exerciseMetricAdds, (add) => add.workoutExerciseId);
    const setAddsByExerciseId = groupBy(setAdds, (add) => add.workoutExerciseId);
    const setEditsByExerciseId = groupBy(setEdits, (edit) => edit.workoutExerciseId);
    const setMetricsBySetId = groupBy(setMetrics, (metric) => metric.workoutSetId);
    return {
        sectionAdds: sectionAdds.concat(),
        sectionEditsById: new Map(sectionEdits.map((s) => [s.workoutSectionId, s])),
        removeSectionIds: new Set(removals.filter((r) => r.entityType === 'section').map((r) => r.entityId)),
        supersetAddsBySectionId,
        supersetEditsById: new Map(supersetEdits.map((s) => [s.workoutSupersetId, s])),
        removeSupersetIds: new Set(removals
            .filter((r) => r.entityType === 'superset')
            .map((r) => r.entityId)),
        supersetExerciseAddsBySupersetId,
        supersetExerciseEditsById: new Map(supersetExerciseEdits.map((s) => [s.workoutSupersetExerciseId, s])),
        removeSupersetExerciseIds: new Set(removals
            .filter((r) => r.entityType === 'supersetExercise')
            .map((r) => r.entityId)),
        alternateGroupAddsBySectionId,
        alternateGroupEditsById: new Map(alternateGroupEdits.map((a) => [a.workoutAlternateGroupId, a])),
        removeAlternateGroupIds: new Set(removals
            .filter((r) => r.entityType === 'alternateGroup')
            .map((r) => r.entityId)),
        alternateExerciseAddsByAlternateGroupId,
        alternateExerciseEditsById: new Map(alternateExerciseEdits.map((a) => [a.workoutAlternateExerciseId, a])),
        removeAlternateExerciseIds: new Set(removals
            .filter((r) => r.entityType === 'alternateExercise')
            .map((r) => r.entityId)),
        exerciseAddsBySectionId,
        exerciseEditById: new Map(exerciseEdits.map((e) => [e.workoutExerciseId, e])),
        removeExerciseIds: new Set(removals
            .filter((r) => r.entityType === 'exercise')
            .map((r) => r.entityId)),
        exerciseMetricAddsByExerciseId,
        removeExerciseMetricIds: new Set(removals
            .filter((r) => r.entityType === 'exerciseMetric')
            .map((r) => r.entityId)),
        setAddsByExerciseId,
        setEditsByExerciseId,
        removeSetIds: new Set(removals.filter((r) => r.entityType === 'set').map((r) => r.entityId)),
        setMetricsBySetId,
    };
}
//# sourceMappingURL=merge-workout-layers.js.map